import { changed, noOp, rejected, type Command, type CommandExecution } from './commands';
import { damage } from './damage';
import { createGroupLayer, findLayer, findLayerParent, layerRetainedBytes, type EditorDocument, type GroupLayer, type Layer, type LayerId } from './model';

export const SMART_OBJECT_EXTENSION = 'maipo:smart-object';

export function isSmartObject(layer: Layer | undefined): layer is GroupLayer {
  return layer?.kind === 'group' && Boolean(layer.extensions[SMART_OBJECT_EXTENSION]);
}

export class ConvertToSmartObjectCommand implements Command {
  readonly label = 'Convert to Smart Object';
  private group?: GroupLayer;
  private originals: { layer: Layer; index: number }[] = [];
  private beforeActive?: LayerId[];

  constructor(private readonly layerIds: readonly LayerId[]) {}

  retainedBytes(): number { return 256 + (this.group ? layerRetainedBytes(this.group) : 0); }

  execute(document: EditorDocument): CommandExecution {
    const locations = this.layerIds.map((id) => findLayerParent(document.root, id)).filter((value): value is NonNullable<typeof value> => Boolean(value));
    if (!locations.length) return noOp();
    if (!locations.every((location) => location.parent === locations[0]?.parent)) return rejected('Smart Object sources must be sibling layers.');
    const parent = locations[0]?.parent;
    if (!parent) return rejected('Layer parent no longer exists.');
    this.beforeActive ??= [...document.activeLayerIds];
    if (!this.group) {
      this.originals = locations.map((location) => ({ layer: location.parent.children[location.index], index: location.index }))
        .filter((item): item is { layer: Layer; index: number } => Boolean(item.layer))
        .sort((left, right) => left.index - right.index);
      if (this.originals.some((item) => isSmartObject(item.layer))) return rejected('Nested Smart Object conversion is not available in this release.');
      const sourceName = this.originals.length === 1 ? this.originals[0]?.layer.name : `${this.originals.length} layers`;
      this.group = createGroupLayer(`${sourceName ?? 'Embedded'} Smart Object`, this.originals.map((item) => item.layer));
      this.group.expanded = false;
      this.group.extensions[SMART_OBJECT_EXTENSION] = { version: 1, embedded: true, sourceLayerCount: this.originals.length };
    }
    for (const item of [...this.originals].sort((left, right) => right.index - left.index)) parent.children.splice(item.index, 1);
    parent.children.splice(this.originals[0]?.index ?? 0, 0, this.group);
    document.activeLayerIds = [this.group.id];
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    if (!this.group) return;
    const location = findLayerParent(document.root, this.group.id);
    if (!location) return;
    location.parent.children.splice(location.index, 1, ...this.originals.map((item) => item.layer));
    document.activeLayerIds = this.beforeActive ? [...this.beforeActive] : this.originals.map((item) => item.layer.id);
  }

  redo(document: EditorDocument): void { this.execute(document); }
  damage() { return damage({ layerIds: [this.group?.id ?? 'root', ...this.layerIds], structural: true, fullDocument: true }); }
}

/** Swaps a Smart Object for a baked raster while retaining the embedded source for exact undo. */
export class RasterizeSmartObjectCommand implements Command {
  readonly label = 'Rasterize Smart Object';
  private source?: GroupLayer;
  private parentId?: LayerId;
  private index?: number;
  private beforeActive?: LayerId[];

  constructor(private readonly smartObjectId: LayerId, private readonly raster: Layer) {}

  retainedBytes(): number { return 256 + layerRetainedBytes(this.raster) + (this.source ? layerRetainedBytes(this.source) : 0); }

  execute(document: EditorDocument): CommandExecution {
    const source = findLayer(document.root, this.smartObjectId);
    const location = findLayerParent(document.root, this.smartObjectId);
    if (!source || !isSmartObject(source) || !location) return rejected('Smart Object no longer exists.');
    this.beforeActive ??= [...document.activeLayerIds];
    this.source ??= source;
    this.parentId ??= location.parent === document.root ? 'root' : location.parent.id;
    this.index ??= location.index;
    location.parent.children.splice(location.index, 1, this.raster);
    document.activeLayerIds = [this.raster.id];
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    if (!this.source) return;
    const location = findLayerParent(document.root, this.raster.id);
    if (!location) return;
    location.parent.children.splice(location.index, 1, this.source);
    document.activeLayerIds = this.beforeActive ? [...this.beforeActive] : [this.source.id];
  }

  redo(document: EditorDocument): void {
    if (!this.source || this.index === undefined || !this.parentId) return;
    const parent = this.parentId === 'root' ? document.root : findLayer(document.root, this.parentId);
    if (!parent || parent.kind !== 'group') return;
    const sourceIndex = parent.children.findIndex((layer) => layer.id === this.source?.id);
    if (sourceIndex >= 0) parent.children.splice(sourceIndex, 1, this.raster);
    document.activeLayerIds = [this.raster.id];
  }

  damage() { return damage({ layerIds: [this.smartObjectId, this.raster.id], structural: true, fullDocument: true }); }
}
